import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ClipboardList, CheckCircle2, ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const classLabel = (c) => {
  if (c === "LKG" || c === "UKG") return c;
  const n = parseInt(c, 10);
  if (isNaN(n)) return c;
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

const pctTone = (p) =>
  p == null ? "text-slate-400" : p < 75 ? "text-rose-600 dark:text-rose-400" : p < 85 ? "text-amber-600 dark:text-amber-400" : "text-emerald-600 dark:text-emerald-300";

export function AttendanceTodayModal({ open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setExpanded({});
    api.get("/admin/attendance/today-detail")
      .then(({ data }) => {
        setData(data);
        // auto-expand sections that have absentees
        const init = {};
        (data.classes || []).forEach((c) => {
          if (c.marked && c.absent > 0) init[`${c.class_name}-${c.section}`] = true;
        });
        setExpanded(init);
      })
      .catch((e) => { toast.error(apiErr(e.response?.data?.detail) || "Could not load attendance"); setData(null); })
      .finally(() => setLoading(false));
  }, [open]);

  const toggle = (key) => setExpanded((e) => ({ ...e, [key]: !e[key] }));
  const classes = data?.classes || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="attendance-today-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <span className="h-8 w-8 rounded-lg bg-sky-50 dark:bg-sky-900/40 text-sky-600 dark:text-sky-300 flex items-center justify-center">
              <ClipboardList className="h-4 w-4" />
            </span>
            Today's attendance
          </DialogTitle>
          <DialogDescription>
            {loading ? "Loading…" : (
              <>
                <span className="font-semibold text-slate-600 dark:text-slate-300" data-testid="att-marked-summary">{data?.marked_classes ?? 0}/{data?.total_classes ?? 0}</span> classes marked today
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {!loading && (
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl border border-rose-200 dark:border-rose-800 bg-rose-50/60 dark:bg-rose-900/20 p-4" data-testid="att-total-absent-tile">
              <div className="text-[10px] uppercase tracking-[0.15em] text-rose-500/80 dark:text-rose-300/70 mb-1">Total absent today</div>
              <div className="text-3xl font-bold text-rose-600 dark:text-rose-400" style={{ fontFamily: "Outfit" }} data-testid="att-absent-summary">{data?.absent_total ?? 0}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/40 p-4" data-testid="att-total-students-tile">
              <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 mb-1">Total students in school</div>
              <div className="text-3xl font-bold text-slate-700 dark:text-slate-200" style={{ fontFamily: "Outfit" }} data-testid="att-total-students">{data?.total_students ?? 0}</div>
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-y-auto flex-1 min-h-0">
          {loading ? (
            <div className="p-10 text-center text-slate-500 text-sm">Loading attendance…</div>
          ) : classes.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm" data-testid="att-empty">No classes found.</div>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {classes.map((c) => {
                const key = `${c.class_name}-${c.section}`;
                const hasAbsent = c.marked && c.absent > 0;
                const isOpen = !!expanded[key];
                return (
                  <li key={key} data-testid={`att-class-${key}`}>
                    <button
                      type="button"
                      onClick={() => hasAbsent && toggle(key)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left ${hasAbsent ? "hover:bg-slate-50 dark:hover:bg-slate-800/60 cursor-pointer" : "cursor-default"}`}
                      data-testid={`att-class-toggle-${key}`}
                    >
                      <div className="w-16 shrink-0 text-sm font-semibold text-slate-800 dark:text-slate-100">
                        {classLabel(c.class_name)}-{c.section}
                      </div>
                      <div className="flex-1 min-w-0">
                        {c.marked ? (
                          <div className="flex items-center gap-2 flex-wrap">
                            {c.absent > 0 ? (
                              <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-300" data-testid={`att-absent-count-${key}`}>
                                <AlertTriangle className="h-3 w-3" /> {c.absent} absent
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-300">
                                <CheckCircle2 className="h-3 w-3" /> All present
                              </span>
                            )}
                            <span className="text-[11px] text-slate-400">{c.present}/{c.total_students} present</span>
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-300" data-testid={`att-not-marked-${key}`}>
                            Not marked · {c.total_students} students
                          </span>
                        )}
                      </div>
                      {hasAbsent && (isOpen ? <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />)}
                    </button>

                    {hasAbsent && isOpen && (
                      <ul className="px-4 pb-3 space-y-1.5" data-testid={`att-absent-list-${key}`}>
                        {c.absent_students.map((s) => (
                          <li key={s.student_id} className="flex items-center gap-3 rounded-lg bg-slate-50 dark:bg-slate-800/60 px-3 py-2" data-testid={`att-absent-student-${s.student_id}`}>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{s.name}</div>
                              <div className="text-[11px] text-slate-400 font-mono">#{s.roll_number}</div>
                            </div>
                            <div className="text-right shrink-0">
                              <div className={`text-sm font-bold ${pctTone(s.attendance_pct_30d)}`} data-testid={`att-student-pct-${s.student_id}`}>
                                {s.attendance_pct_30d == null ? "—" : `${s.attendance_pct_30d}%`}
                              </div>
                              <div className="text-[10px] text-slate-400">
                                {s.total_days ? `${s.present_days}/${s.total_days} days` : "no records"}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="text-[11px] text-slate-400 shrink-0">Attendance % is over the last {data?.days ?? 30} days. Below 75% shown in red.</div>
      </DialogContent>
    </Dialog>
  );
}
